// src/authpages/dashboard/FriendsList.js
import React, { useState, useEffect } from 'react';
import { 
  db, 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  getDoc, 
  updateDoc, 
  arrayUnion, 
  arrayRemove 
} from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Users, 
  UserPlus, 
  UserMinus, 
  Search, 
  CheckCircle, 
  XCircle,
  BookOpen,
  Mail
} from 'lucide-react';

const FriendsList = () => {
  const { currentUser } = useAuth();
  const [friends, setFriends] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('friends');

  // Fetch friends list, pending requests, and sent requests on component mount
  useEffect(() => {
    if (currentUser) {
      fetchFriendsData();
    }
  }, [currentUser]);

  const fetchFriendsData = async () => {
    try {
      // Get user's document with friends list
      const userDocRef = doc(db, "users", currentUser.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        // Get friends list IDs
        const friendIds = userData.friends || [];
        const pendingIds = userData.pendingFriendRequests || [];
        const sentIds = userData.sentFriendRequests || [];
        
        // Fetch complete friend data
        if (friendIds.length > 0) {
          const friendsData = await Promise.all(
            friendIds.map(async (friendId) => {
              const friendDoc = await getDoc(doc(db, "users", friendId));
              if (friendDoc.exists()) {
                return {
                  id: friendId,
                  ...friendDoc.data()
                };
              }
              return null;
            })
          );
          setFriends(friendsData.filter(Boolean));
        } else {
          setFriends([]);
        }
        
        // Fetch pending requests data
        if (pendingIds.length > 0) {
          const pendingData = await Promise.all(
            pendingIds.map(async (userId) => {
              const userDoc = await getDoc(doc(db, "users", userId));
              if (userDoc.exists()) {
                return {
                  id: userId,
                  ...userDoc.data()
                };
              }
              return null;
            })
          );
          setPendingRequests(pendingData.filter(Boolean));
        } else {
          setPendingRequests([]);
        }
        
        // Fetch sent requests data
        if (sentIds.length > 0) {
          const sentData = await Promise.all(
            sentIds.map(async (userId) => {
              const userDoc = await getDoc(doc(db, "users", userId));
              if (userDoc.exists()) {
                return {
                  id: userId,
                  ...userDoc.data()
                };
              }
              return null;
            })
          );
          setSentRequests(sentData.filter(Boolean));
        } else {
          setSentRequests([]);
        }
      }
    } catch (error) {
      console.error("Error fetching friends data:", error);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setIsSearching(true);
    try {
      // Search for users by email or name
      const emailQuery = query(
        collection(db, "users"),
        where("email", "==", searchQuery.trim().toLowerCase())
      );
      
      const nameQuery = query(
        collection(db, "users"),
        where("fullName", ">=", searchQuery.trim()),
        where("fullName", "<=", searchQuery.trim() + "\uf8ff")
      );
      
      const [emailResults, nameResults] = await Promise.all([
        getDocs(emailQuery),
        getDocs(nameQuery)
      ]);
      
      const results = new Map();
      
      // Process email results
      emailResults.forEach((doc) => {
        if (doc.id !== currentUser.uid) {
          results.set(doc.id, { id: doc.id, ...doc.data() });
        }
      });
      
      // Process name results
      nameResults.forEach((doc) => {
        if (doc.id !== currentUser.uid) {
          results.set(doc.id, { id: doc.id, ...doc.data() });
        }
      });
      
      // Convert Map to array
      const searchResultsArray = Array.from(results.values());
      
      // Filter out existing friends and pending requests
      const friendIds = friends.map(friend => friend.id);
      const pendingIds = pendingRequests.map(request => request.id);
      const sentIds = sentRequests.map(request => request.id);
      
      const filteredResults = searchResultsArray.filter(user => 
        !friendIds.includes(user.id) && 
        !pendingIds.includes(user.id) &&
        !sentIds.includes(user.id)
      );
      
      setSearchResults(filteredResults);
    } catch (error) {
      console.error("Error searching for users:", error);
    } finally {
      setIsSearching(false);
    }
  };

  const sendFriendRequest = async (userId) => {
    try {
      // Update current user's sent requests
      await updateDoc(doc(db, "users", currentUser.uid), {
        sentFriendRequests: arrayUnion(userId)
      });
      
      // Update target user's pending requests
      await updateDoc(doc(db, "users", userId), {
        pendingFriendRequests: arrayUnion(currentUser.uid)
      });
      
      // Update local state
      const userToAdd = searchResults.find(user => user.id === userId);
      setSentRequests([...sentRequests, userToAdd]);
      
      // Remove from search results
      setSearchResults(searchResults.filter(user => user.id !== userId));
    } catch (error) {
      console.error("Error sending friend request:", error);
    }
  };

  const acceptFriendRequest = async (userId) => {
    try {
      // Update both users' friends lists
      await updateDoc(doc(db, "users", currentUser.uid), {
        friends: arrayUnion(userId),
        pendingFriendRequests: arrayRemove(userId)
      });
      
      await updateDoc(doc(db, "users", userId), {
        friends: arrayUnion(currentUser.uid),
        sentFriendRequests: arrayRemove(currentUser.uid)
      });
      
      // Update local state
      const newFriend = pendingRequests.find(request => request.id === userId);
      setFriends([...friends, newFriend]);
      setPendingRequests(pendingRequests.filter(request => request.id !== userId));
      
      // Refresh friends data to ensure everything is in sync
      fetchFriendsData();
    } catch (error) {
      console.error("Error accepting friend request:", error);
    }
  };

  const rejectFriendRequest = async (userId) => {
    try {
      // Remove from pending requests
      await updateDoc(doc(db, "users", currentUser.uid), {
        pendingFriendRequests: arrayRemove(userId)
      });
      
      // Remove from sent requests of other user
      await updateDoc(doc(db, "users", userId), {
        sentFriendRequests: arrayRemove(currentUser.uid)
      });
      
      // Update local state
      setPendingRequests(pendingRequests.filter(request => request.id !== userId));
    } catch (error) {
      console.error("Error rejecting friend request:", error);
    }
  };

  const cancelFriendRequest = async (userId) => {
    try {
      // Remove from sent requests
      await updateDoc(doc(db, "users", currentUser.uid), {
        sentFriendRequests: arrayRemove(userId)
      });
      
      // Remove from pending requests of other user
      await updateDoc(doc(db, "users", userId), {
        pendingFriendRequests: arrayRemove(currentUser.uid)
      });
      
      // Update local state
      setSentRequests(sentRequests.filter(request => request.id !== userId));
    } catch (error) {
      console.error("Error canceling friend request:", error);
    }
  };

  const removeFriend = async (userId) => {
    try {
      // Remove from both users' friends lists
      await updateDoc(doc(db, "users", currentUser.uid), {
        friends: arrayRemove(userId)
      });
      
      await updateDoc(doc(db, "users", userId), {
        friends: arrayRemove(currentUser.uid)
      });
      
      // Update local state
      setFriends(friends.filter(friend => friend.id !== userId));
    } catch (error) {
      console.error("Error removing friend:", error);
    }
  };

  const renderUserCard = (user, type) => {
    return (
      <div key={user.id} className="bg-white rounded-lg shadow-sm p-4 flex items-center justify-between">
        <div className="flex items-center">
          <div className="bg-blue-100 rounded-full p-3">
            <Users className="h-6 w-6 text-blue-500" />
          </div>
          <div className="ml-4">
            <h3 className="font-semibold text-gray-800">{user.fullName}</h3>
            <div className="flex items-center text-sm text-gray-500">
              <Mail className="h-4 w-4 mr-1" />
              <span>{user.email}</span>
            </div>
            {user.degreeTitle && (
              <div className="flex items-center text-sm text-gray-500 mt-1">
                <BookOpen className="h-4 w-4 mr-1" />
                <span>{user.degreeTitle}</span>
              </div>
            )}
            {user.verified && (
              <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <CheckCircle className="h-3 w-3 mr-1" /> Verified
              </span>
            )}
          </div>
        </div>
        
        <div>
          {type === 'search' && (
            <button 
              onClick={() => sendFriendRequest(user.id)}
              className="flex items-center text-sm bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-md"
            >
              <UserPlus className="h-4 w-4 mr-1" /> Add Friend
            </button>
          )}
          
          {type === 'pending' && (
            <div className="flex space-x-2">
              <button 
                onClick={() => acceptFriendRequest(user.id)}
                className="flex items-center text-sm bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded-md"
              >
                <CheckCircle className="h-4 w-4 mr-1" /> Accept
              </button>
              <button 
                onClick={() => rejectFriendRequest(user.id)}
                className="flex items-center text-sm bg-gray-200 hover:bg-gray-300 text-gray-800 px-3 py-1 rounded-md"
              >
                <XCircle className="h-4 w-4 mr-1" /> Decline
              </button>
            </div>
          )}
          
          {type === 'sent' && (
            <button 
              onClick={() => cancelFriendRequest(user.id)}
              className="flex items-center text-sm bg-gray-200 hover:bg-gray-300 text-gray-800 px-3 py-1 rounded-md"
            >
              <XCircle className="h-4 w-4 mr-1" /> Cancel
            </button>
          )}
          
          {type === 'friend' && (
            <button 
              onClick={() => removeFriend(user.id)}
              className="flex items-center text-sm bg-red-100 hover:bg-red-200 text-red-600 px-3 py-1 rounded-md"
            >
              <UserMinus className="h-4 w-4 mr-1" /> Remove
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-2xl font-semibold mb-6 text-blue-600 flex items-center">
        <Users className="mr-3" /> Friends
      </h2>
      
      {/* Search Bar */}
      <div className="mb-6">
        <div className="flex">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name or email"
            className="flex-1 p-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={handleSearch}
            disabled={isSearching || !searchQuery.trim()}
            className="bg-blue-500 text-white px-4 py-2 rounded-r-md hover:bg-blue-600 disabled:opacity-50"
          >
            {isSearching ? 'Searching...' : <Search className="h-5 w-5" />}
          </button>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="flex mb-6 bg-gray-100 rounded-lg p-1">
        {[
          { key: 'friends', label: 'My Friends', count: friends.length },
          { key: 'pending', label: 'Pending Requests', count: pendingRequests.length },
          { key: 'sent', label: 'Sent Requests', count: sentRequests.length }
        ].map(({ key, label, count }) => (
          <button
            key={key}
            className={`flex-1 py-2 px-4 flex items-center justify-center ${
              activeTab === key ? 'bg-blue-500 text-white' : 'text-gray-600'
            } ${count > 0 && activeTab !== key ? 'relative' : ''}`}
            onClick={() => setActiveTab(key)}
          >
            {label}
            {count > 0 && (
              <span className={`ml-2 px-1.5 py-0.5 text-xs rounded-full ${
                activeTab === key ? 'bg-white text-blue-500' : 'bg-blue-500 text-white'
              }`}>
                {count}
              </span>
            )}
          </button>
        ))}
      </div>
      
      {/* Search Results */}
      {searchResults.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-medium mb-3 text-gray-700">Search Results</h3>
          <div className="space-y-3">
            {searchResults.map(user => renderUserCard(user, 'search'))}
          </div>
        </div>
      )}
      
      {/* Content based on active tab */}
      {activeTab === 'friends' && (
        <div className="space-y-3">
          {friends.length > 0 ? (
            friends.map(friend => renderUserCard(friend, 'friend'))
          ) : (
            <p className="text-center text-gray-600 py-6">
              You haven't added any friends yet. Use the search bar to find friends.
            </p>
          )}
        </div>
      )}
      
      {activeTab === 'pending' && (
        <div className="space-y-3">
          {pendingRequests.length > 0 ? (
            pendingRequests.map(request => renderUserCard(request, 'pending'))
          ) : (
            <p className="text-center text-gray-600 py-6">
              You don't have any pending friend requests.
            </p>
          )}
        </div>
      )}
      
      {activeTab === 'sent' && (
        <div className="space-y-3">
          {sentRequests.length > 0 ? (
            sentRequests.map(request => renderUserCard(request, 'sent'))
          ) : (
            <p className="text-center text-gray-600 py-6">
              You haven't sent any friend requests.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default FriendsList;